/*
 * Zoom images
 *
 * Opens the product media in a Photoswipe lightbox. Media hidden for the selected color
 * (`.media--filtered`, see product-variant-media.js) is left out of the gallery and its thumbnails.
 */
(function () {
  'use strict';

  const selectors = {
    container: '[data-section-id]',
    productInfo: 'product-info',
    productImages: 'product-images',
    zoomImage: '[data-zoom-image]',
    zoomCaption: '[data-zoom-caption]',
    thumbsContainer: '.pswp__thumbs',
    thumbsTemplate: '[data-pswp-thumbs-template]',
    thumb: '[data-pswp-thumb-media]',
    mediaItem: '[data-image-id]',
  };

  const classes = {
    dragging: 'is-dragging',
    filtered: 'media--filtered',
    soldOut: 'variant--soldout',
    unavailable: 'variant--unavailable',
  };

  const attributes = {
    imageSrc: 'data-image-src',
    imageWidth: 'data-image-width',
    imageHeight: 'data-image-height',
    imageId: 'data-image-id',
    thumbMedia: 'data-pswp-thumb-media',
  };

  class ZoomImages extends HTMLElement {
    constructor() {
      super();

      this.container = this.closest(selectors.container);
      this.images = this.querySelectorAll(selectors.zoomImage);
      this.zoomCaptions = this.container.querySelector(selectors.zoomCaption);
      this.thumbsContainer = document.querySelector(selectors.thumbsContainer);
    }

    connectedCallback() {
      this.images.forEach((image) => {
        image.addEventListener('click', (event) => {
          event.preventDefault();

          if (image.closest(selectors.productImages).classList.contains(classes.dragging)) return;

          this.createZoom(image);
          window.a11y.lastElement = image;
        });

        image.addEventListener('keyup', (event) => {
          if (event.code !== 'Enter') return;

          event.preventDefault();
          this.createZoom(image);
          window.a11y.lastElement = image;
        });
      });
    }

    getVisibleImages() {
      return [...this.images].filter((image) => !image.closest(`.${classes.filtered}`));
    }

    getThumbsHTML(visibleImages) {
      const template = this.container.querySelector(selectors.thumbsTemplate);
      if (!template) return '';

      const visibleIds = visibleImages.map((image) => image.closest(selectors.mediaItem)?.getAttribute(attributes.imageId));
      const holder = document.createElement('div');
      holder.appendChild(template.content.cloneNode(true));

      holder.querySelectorAll(selectors.thumb).forEach((thumb) => {
        if (!visibleIds.includes(thumb.getAttribute(attributes.thumbMedia))) thumb.remove();
      });

      return holder.innerHTML;
    }

    createZoom(clickedImage) {
      const visibleImages = this.getVisibleImages();
      const index = Math.max(visibleImages.indexOf(clickedImage), 0);
      const thumbsHTML = this.getThumbsHTML(visibleImages);
      const colorScheme = this.getColorScheme();

      const items = visibleImages.map((image) => {
        const src = image.getAttribute(attributes.imageSrc);

        return {
          src: src,
          w: parseInt(image.getAttribute(attributes.imageWidth)),
          h: parseInt(image.getAttribute(attributes.imageHeight)),
          msrc: src,
        };
      });

      const options = {
        history: false,
        focus: false,
        index: index,
        mainClass: `pswp-zoom-gallery${items.length === 1 ? ' pswp-zoom-gallery--single' : ''}${colorScheme ? ` ${colorScheme}` : ''}`,
        showHideOpacity: true,
        howAnimationDuration: 150,
        hideAnimationDuration: 250,
        closeOnScroll: false,
        closeOnVerticalDrag: false,
        captionEl: true,
        closeEl: true,
        closeElClasses: ['caption-close', 'title'],
        tapToClose: false,
        clickToCloseNonZoomable: false,
        maxSpreadZoom: 2,
        loop: true,
        spacing: 0,
        allowPanToNext: true,
        pinchToClose: false,
        addCaptionHTMLFn: (item, captionEl, isFake) => {
          this.zoomCaption(item, captionEl, isFake);
        },
        getThumbBoundsFn: () => {
          const image = visibleImages[index];
          const pageYScroll = window.scrollY || document.documentElement.scrollTop;
          const rect = image.getBoundingClientRect();

          return {x: rect.left, y: rect.top + pageYScroll, w: rect.width};
        },
      };

      new window.theme.LoadPhotoswipe(items, options);

      if (this.thumbsContainer && thumbsHTML !== '') {
        this.thumbsContainer.innerHTML = thumbsHTML;
      }
    }

    zoomCaption(item, captionEl) {
      let captionHTML = '';
      const caption = captionEl.children[0];

      if (this.zoomCaptions) {
        captionHTML = this.zoomCaptions.innerHTML;
        caption.classList.toggle(classes.soldOut, Boolean(this.zoomCaptions.closest(`.${classes.soldOut}`)));
        caption.classList.toggle(classes.unavailable, Boolean(this.zoomCaptions.closest(`.${classes.unavailable}`)));
      }

      caption.innerHTML = captionHTML;

      return false;
    }

    getColorScheme() {
      const productInfo = this.closest(selectors.productInfo);

      return Array.from(productInfo.classList).find((className) => className.startsWith('color-scheme'));
    }
  }

  customElements.get('zoom-images') || customElements.define('zoom-images', ZoomImages);
})();
